if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        window.addEventListener('resize', () => {
          if (this.hasAttribute('open')) this.fitDescription();
        });
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      hide(preventFocus = false) {
        if (this.closing) return;

        const finish = () => {
          this.closing = false;
          this.classList.remove('qa-closing', 'qa-opening');

          const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          if (cartNotification) cartNotification.setActiveElement(this.openedBy);
          this.modalContent.innerHTML = '';

          if (preventFocus) this.openedBy = null;
          super.hide();
        };

        if (!this.hasAttribute('open') || this.prefersReducedMotion()) {
          finish();
          return;
        }

        // Shrink back toward where it opened from, then actually close.
        this.closing = true;
        this.classList.remove('qa-opening');
        this.classList.add('qa-closing');
        setTimeout(finish, 190);
      }

      // The popup expands out of the cart button that was pressed (transform-origin set from the button).
      animateOpen(opener) {
        if (this.prefersReducedMotion()) return;

        const content = this.querySelector('.quick-add-modal__content');
        if (!content || !opener) return;

        const contentRect = content.getBoundingClientRect();
        const openerRect = opener.getBoundingClientRect();
        content.style.setProperty('--qa-ox', `${Math.round(openerRect.left + openerRect.width / 2 - contentRect.left)}px`);
        content.style.setProperty('--qa-oy', `${Math.round(openerRect.top + openerRect.height / 2 - contentRect.top)}px`);

        this.classList.remove('qa-closing');
        this.classList.add('qa-opening');

        const done = () => {
          content.removeEventListener('animationend', onEnd);
          this.classList.remove('qa-opening');
          this.fitDescription();
        };
        const onEnd = (event) => {
          if (event.target === content) done();
        };
        content.addEventListener('animationend', onEnd);
        setTimeout(done, 700);
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            // Measure before the opening animation scales the popup, then animate.
            this.fitDescription();
            this.animateOpen(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.addShortDescription(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      addShortDescription(productElement) {
        if (productElement.querySelector('.quick-add__description')) return;

        const source = productElement.querySelector('.product__description');
        const infoContainer = productElement.querySelector('.product__info-container');
        if (!source || !infoContainer) return;

        const html = this.cleanDescriptionHTML(source);
        if (!html) return;

        const buildDescription = (modifier, linkLabel) => {
          const description = document.createElement('div');
          description.className = `quick-add__description quick-add__description--${modifier}`;

          const body = document.createElement('div');
          body.className = 'quick-add__description-body';
          body.innerHTML = html;
          description.appendChild(body);

          if (productElement.dataset.url) {
            const link = document.createElement('a');
            link.href = productElement.dataset.url;
            link.className = 'quick-add__description-link link';
            link.textContent = linkLabel;
            description.appendChild(link);
          }
          return description;
        };

        // Two copies, one shown per breakpoint in CSS, so the position follows the screen width
        // (under the image on desktop, after the options on mobile) even if the window is resized.
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (mediaWrapper) mediaWrapper.append(buildDescription('media', 'See more...'));
        infoContainer.append(buildDescription('bottom', 'View full details'));
      }

      // Keeps the description's real structure (paragraphs, headings, bold, lists, spacing) but drops
      // the noise that made it look doubled: attributes, <p> wrapped inside <li>, empty lines and
      // nested duplicate inline tags.
      cleanDescriptionHTML(source) {
        const allowed = new Set(['P', 'STRONG', 'B', 'EM', 'I', 'BR', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HR']);
        const root = source.cloneNode(true);

        const unwrap = (node) => {
          while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
          node.remove();
        };

        // Strip attributes; unwrap or drop anything that is not allowed.
        Array.from(root.querySelectorAll('*')).forEach((node) => {
          if (!node.parentNode) return;
          if (['SCRIPT', 'STYLE', 'IFRAME', 'IMG', 'VIDEO', 'FORM', 'BUTTON', 'INPUT', 'SVG'].includes(node.tagName)) {
            node.remove();
          } else if (!allowed.has(node.tagName)) {
            unwrap(node);
          } else {
            Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name));
          }
        });

        // <li><p>text</p></li> renders as a double-spaced item: keep just the text.
        root.querySelectorAll('li > p').forEach(unwrap);

        // Nested duplicates such as <i><i>text</i></i> or <strong><strong>..</strong></strong>.
        root.querySelectorAll('strong, b, em, i').forEach((node) => {
          if (!node.parentNode) return;
          const only = node.children.length === 1 && node.childNodes.length === 1 ? node.firstElementChild : null;
          if (only && only.tagName === node.tagName) unwrap(only);
          if (node.parentNode && node.parentNode.tagName === node.tagName) unwrap(node);
        });

        // Empty lines: elements with no text, no <br> and no <hr> inside.
        let removed = true;
        while (removed) {
          removed = false;
          root.querySelectorAll('p, li, ul, ol, strong, b, em, i, h1, h2, h3, h4, h5, h6').forEach((node) => {
            if (!node.parentNode) return;
            if (!node.textContent.trim() && !node.querySelector('hr')) {
              node.remove();
              removed = true;
            }
          });
        }

        // Stray <br> at the start/end of blocks and runs of them.
        root.querySelectorAll('br').forEach((br) => {
          const prev = br.previousSibling;
          const next = br.nextSibling;
          const isBlank = (n) => !n || (n.nodeType === Node.TEXT_NODE && !n.textContent.trim());
          if (isBlank(prev) || isBlank(next) || (prev && prev.nodeName === 'BR')) br.remove();
        });

        // No leading/trailing or doubled rules.
        const rules = Array.from(root.querySelectorAll('hr'));
        rules.forEach((hr) => {
          const prev = hr.previousElementSibling;
          if (!prev || prev.tagName === 'HR' || !hr.nextElementSibling) hr.remove();
        });

        return root.innerHTML.trim();
      }

      // Show only whole blocks (paragraph / list item / heading) that fit in `limit` px, so text is
      // never cut through a line; returns true when something was left out.
      clampDescriptionBody(body, limit) {
        body.style.maxHeight = '';
        if (body.scrollHeight <= limit + 1) return false;

        const top = body.getBoundingClientRect().top;
        const blocks = Array.from(body.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, hr'));
        let fit = 0;
        blocks.forEach((block) => {
          const bottom = block.getBoundingClientRect().bottom - top;
          if (bottom <= limit + 1 && bottom > fit) fit = bottom;
        });
        body.style.maxHeight = `${Math.round(fit > 0 ? fit : limit)}px`;
        return true;
      }

      // Desktop: the left column does not scroll, so show as many whole blocks as fit under the image.
      // Mobile: a fixed-height preview after the options.
      fitDescription() {
        this.querySelectorAll('.quick-add__description').forEach((description) => {
          if (description.offsetParent === null) return;

          const body = description.querySelector('.quick-add__description-body');
          const link = description.querySelector('a');
          if (!body) return;

          let limit;
          if (description.classList.contains('quick-add__description--media')) {
            const column = description.parentElement;
            const linkSpace = link ? link.getBoundingClientRect().height + 12 : 0;
            const columnBottom = column.getBoundingClientRect().bottom - parseFloat(getComputedStyle(column).paddingBottom || 0);
            body.style.maxHeight = '';
            limit = columnBottom - body.getBoundingClientRect().top - linkSpace;
            limit = Math.max(limit, 60);
          } else {
            limit = 220;
          }

          const truncated = this.clampDescriptionBody(body, limit);
          if (link) link.hidden = !truncated && description.classList.contains('quick-add__description--media');
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
